import React, { useCallback, useEffect, useMemo, useState } from 'react';
import qs from 'qs';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '@mui/material/Icon';
import Container from '@mui/material/Container';
import MKBox from 'components/MaterialKit/MKBox';
import Button from 'components/Button';
import Image from 'components/Image';
import { useMenu } from 'contexts/menu';
import Select from 'components/Select';
import { initialAuthState, useAuth } from 'contexts/auth';
import MKTypography from 'components/MaterialKit/MKTypography';
import { logout } from 'api/auth';
import { handleErrorResponse } from 'utils/general';
// eslint-disable-next-line import/no-cycle
import { LANGUAGE_COLLECTION_DEFINITION_ID } from 'pages/AppSummary/LanguageSection';
import { getPublicCollectionDefinition } from 'api/collection_definitions';
import { getPublicDynamicTableRows } from 'api/sections';
import { getDynamicData } from 'utils/sections';
import { useTranslation } from 'react-i18next';
import { getLocaleMap } from 'utils/locales';
import MobileMenu from './MobileMenu';

const BuddhistPageHeaderSection = ({
  show_menu,
  show_language,
  show_branch,
  show_display_name,
  show_login_logout_button,
  show_icon,
  icon_url,
  icon_width,
  background_color,
  branches_config,
  ...props
}) => {
  const { auth, setAuth } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [branch, setBranch] = useState();
  const { menu } = useMenu();
  const { menu_node } = menu || {};
  const { menu_items } = menu_node || {};
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = qs.parse((location.search || '').slice(1));
  const [languageData, setLanguageData] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState();
  const languageOptions = useMemo(() => {
    const options = [];
    languageData.forEach((datum) => {
      localStorage.setItem(datum.value, datum.id);
      options.push({
        label: datum.name,
        value: datum.value,
      });
    });
    return options;
  }, [languageData]);
  const { i18n: { changeLanguage, language } } = useTranslation();

  const [lm, setLm] = useState({});
  useEffect(() => {
    getLocaleMap(['logout', 'login'])
      .then((response) => {
        setLm(response);
      });
  }, []);

  useEffect(() => {
    if (languageOptions.length > 0) {
      setCurrentLanguage(language);
    }
  }, [language, languageOptions]);

  useEffect(() => {
    const currentBranch = localStorage.getItem('branch');
    if (currentBranch) {
      setBranch(currentBranch);
    } else if (branches_config.length > 0) {
      setBranch(branches_config[0].value);
      localStorage.setItem('branch', branches_config[0].value);
    }
  }, [branches_config, language, languageOptions]);

  const handleChangeLanguage = (newLanguage) => {
    localStorage.setItem('i18nextLng', newLanguage);
    changeLanguage(newLanguage);
    setCurrentLanguage(newLanguage);
    window.location.reload();
  };

  const handleChangeBranch = (newBranch) => {
    localStorage.setItem('branch', newBranch);
    setBranch(newBranch);
    window.location.reload();
  };

  const handleLogout = useCallback(() => {
    return logout()
      .then(() => {
        setAuth(initialAuthState);
        navigate('/login');
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
  }, [navigate, setAuth]);

  const fetchLanguageFromApi = useCallback(() => {
    return getPublicCollectionDefinition(LANGUAGE_COLLECTION_DEFINITION_ID, { $expand: 'attributes' })
      .then((res) => {
        const { attributes } = res.data;
        return getPublicDynamicTableRows(LANGUAGE_COLLECTION_DEFINITION_ID)
          .then(({ data }) => {
            const dynamicFieldValueData = data.map((datum) => ({
              ...getDynamicData(datum, attributes),
              ...datum,
            }));
            setLanguageData(dynamicFieldValueData);
          })
          .catch((err) => {
            handleErrorResponse(err, setAuth);
          });
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [setAuth]);

  useEffect(() => {
    fetchLanguageFromApi();
  }, [fetchLanguageFromApi]);

  return (
    <MKBox
      px={{ xs: 0, md: 1 }}
      bgColor={background_color}
      {...props}
    >
      <Container>
        <MKBox display="flex" justifyContent="center" alignItems="center">
          <MKBox display={{ xs: 'none', lg: 'flex' }}>
            {show_menu && (
              <MKBox
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                flexWrap="wrap"
              >
                {(menu_items || []).map((menuItem, idx) => {
                  const { label: menuItemLabel, path, disabled } = menuItem;
                  return !disabled ? (
                    <Button
                      key={`${idx}-${path}`}
                      to={`${path}?${qs.stringify(pick(queryParams, 'mode'))}`}
                      variant="text"
                      color={location.pathname === path ? 'orange' : 'white'}
                      fontSize={16}
                      size="small"
                      sx={{
                        ...(location.pathname === path && { 'border-bottom': '5px solid orange' }),
                        borderRadius: 0,
                        py: 3,
                      }}
                    >
                      {menuItemLabel}
                    </Button>
                  ) : null;
                })}
              </MKBox>
            )}
            {show_branch && (
              <MKBox
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                flexWrap="wrap"
                mr={1}
              >
                <Select
                  value={branch}
                  onChange={handleChangeBranch}
                  options={branches_config}
                  sx={{
                    'borderRadius': 5,
                    '.MuiSelect-select': {
                      color: 'white !important',
                    },
                    '.MuiSvgIcon-root ': {
                      fill: 'white !important',
                    },
                  }}
                />
              </MKBox>
            )}
            {show_language && (
              <MKBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                mr={2}
              >
                <Select
                  value={currentLanguage}
                  onChange={handleChangeLanguage}
                  options={languageOptions}
                  sx={{
                    'borderRadius': 5,
                    '.MuiSelect-select': {
                      color: 'white !important',
                    },
                    '.MuiSvgIcon-root ': {
                      fill: 'white !important',
                    },
                  }}
                />
              </MKBox>
            )}
            <MKBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              mr={0.5}
            >
              {auth?.user ? (
                <MKBox
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  color="white"
                >
                  {show_display_name && (
                    <MKTypography fontSize={16} color="white" fontWeight="bold">
                      {auth?.user.display_name}
                    </MKTypography>
                  )}
                  {show_login_logout_button && (
                    <Button
                      variant="text"
                      color="white"
                      fontSize={16}
                      size="small"
                      onClick={handleLogout}
                    >
                      {`[${lm.logout || 'Logout'}]`}
                    </Button>
                  )}
                  {show_icon && (
                    <MKBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ cursor: 'pointer' }}
                    >
                      <Image src={icon_url} width={icon_width} />
                    </MKBox>
                  )}
                </MKBox>
              )
                : (
                  <Button
                    to="/login"
                    variant="text"
                    color="white"
                    fontSize={16}
                    size="small"
                    sx={{
                      'backgroundColor': 'orange',
                      'borderRadius': 5,
                      '&:hover': {
                        backgroundColor: 'orange',
                      },
                    }}
                  >
                    {`[${lm.login || 'Login'}]`}
                  </Button>
                )}
            </MKBox>
          </MKBox>
          <MKBox
            display={{ xs: 'flex', lg: 'none' }}
            lineHeight={0}
            pl={1.5}
            sx={{ cursor: 'pointer' }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Icon fontSize="default" style={{ color: 'white' }}>
              {mobileMenuOpen ? 'close' : 'menu'}
            </Icon>
          </MKBox>
        </MKBox>
        <MKBox
          shadow="lg"
          borderRadius="xl"
          display={{ xs: 'flex', lg: 'none' }}
          px={2}
          bgColor="white"
        >
          <MobileMenu
            menuItems={menu_items}
            open={mobileMenuOpen}
            setOpen={setMobileMenuOpen}
          />
        </MKBox>
      </Container>
    </MKBox>
  );
};

BuddhistPageHeaderSection.propTypes = {
  show_menu: PropTypes.bool,
  show_language: PropTypes.bool,
  show_branch: PropTypes.bool,
  show_display_name: PropTypes.bool,
  show_login_logout_button: PropTypes.bool,
  show_icon: PropTypes.bool,
  icon_url: PropTypes.string,
  icon_width: PropTypes.string,
  background_color: PropTypes.string,
  branches_config: PropTypes.array,
};

BuddhistPageHeaderSection.defaultProps = {
  show_menu: true,
  show_language: true,
  show_branch: true,
  show_display_name: true,
  show_login_logout_button: true,
  show_icon: true,
  icon_url: '',
  icon_width: '',
  background_color: '',
  branches_config: [],
};

export default BuddhistPageHeaderSection;
